import { Link, router } from "@inertiajs/react"
import { Sparkles } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { creatorDormancyBannerPath, creatorMissionDetailPath } from "@/routes"
import type { DormancyNudge } from "@/types/collab"

export interface DormancyNudgeBannerProps {
  /** The dormancy nudge prop from `Creator/Missions`. `null`/absent → hidden. */
  nudge: DormancyNudge | null | undefined
  /**
   * OKS-435 — invoked once after the OPTIMISTIC collapse so the parent page
   * can move keyboard focus into the missions list. The banner unmounts
   * itself on dismiss (`return null`), which would otherwise drop focus to
   * `<body>`; the focus target lives in `Creator/Missions.tsx`, so the parent
   * owns the move. Deliberately NOT called on the error re-show path (focus
   * has already moved; the Sonner toast announces via aria-live).
   */
  onDismissed?: () => void
}

/**
 * Format a `YYYY-MM-DD` deadline into a short, locale-aware "Mon D" string
 * ("May 22" / "22 mai"). Anchored at local midday so the civil date can't
 * slip across a timezone boundary (mirrors `welcome-header.tsx`). Returns
 * `null` for a missing/malformed value so the caller omits the Due segment.
 */
function formatDeadline(
  deadlineOn: string | null,
  locale: string,
): string | null {
  if (!deadlineOn || !/^\d{4}-\d{2}-\d{2}$/.test(deadlineOn)) return null
  const date = new Date(`${deadlineOn}T12:00:00`)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
  }).format(date)
}

/**
 * DormancyNudgeBanner — OKS-403 (W25 / UX Spec §2).
 *
 * A dismissible re-engagement banner rendered as the FIRST child of the
 * missions-home column, directly ABOVE `<BrandCoverBand>`, ONLY when the
 * backend ships a `dormancy_nudge` object (creator dormant ≥14d on the
 * current brand, not dismissed this window, AND a suggestable mission
 * exists). Returns `null` (no DOM) on a null/absent prop OR after an
 * optimistic local dismiss.
 *
 * Content (UX Spec §2): "{BRAND} MISSED YOU" headline → "Welcome back,
 * {firstName}" → a one-line mission preview ("{format} · {title} · Due
 * {date} · +{xp} XP", Due omitted when no deadline) → solid "See the
 * mission" (→ `creatorMissionDetailPath(collab_id)`) + ghost "Not now".
 *
 * Dismiss is OPTIMISTIC + server-persisted (NO localStorage, Crew rule):
 * tapping "Not now" hides the banner immediately and PATCHes
 * `creatorDormancyBannerPath()` in the background. On persist failure the
 * banner re-shows and a Sonner toast invites a retry (UX Spec §3 error
 * state). The success redirect recomputes the prop server-side (now null),
 * so the banner stays gone on the next paint.
 *
 * Brand isolation: every value (brand name, mission, collab_id) is
 * backend-scoped to the active membership — the component renders props
 * verbatim and never reaches across brands.
 *
 * Accessibility: labelled `role="region"` (UX Spec §6); both CTAs are real
 * buttons; the headline carries the brand name in text (state is conveyed
 * by content, not the accent treatment alone).
 */
export function DormancyNudgeBanner({
  nudge,
  onDismissed,
}: DormancyNudgeBannerProps) {
  const { t, i18n } = useTranslation()
  const [dismissed, setDismissed] = useState(false)

  if (!nudge || dismissed) return null

  const { brand_name, first_name, mission } = nudge

  const formatLabel =
    mission.content_format === "other" && mission.content_format_other
      ? mission.content_format_other
      : t(`missions.formats.${mission.content_format}`, {
          defaultValue: mission.content_format,
        })
  const dueLabel = formatDeadline(mission.deadline_on, i18n.language)

  function handleDismiss() {
    // Optimistic collapse — hide immediately, persist in the background.
    setDismissed(true)
    // OKS-435 — surface the dismiss so the parent moves keyboard focus into
    // the missions list BEFORE our unmount would drop it to `<body>`. The
    // target is a mounted sibling, so a synchronous focus move is safe.
    onDismissed?.()
    router.patch(
      creatorDormancyBannerPath(),
      {},
      {
        preserveScroll: true,
        onError: () => {
          // Persist failed — re-show the banner and prompt a retry. The
          // page itself is never blocked (UX Spec §3 error state).
          setDismissed(false)
          toast.error(t("creator_home.dormancy_nudge.dismiss_error"))
        },
      },
    )
  }

  return (
    <section
      role="region"
      aria-label={t("creator_home.dormancy_nudge.region_label")}
      data-testid="dormancy-nudge-banner"
      className="bg-accent text-accent-foreground flex flex-col gap-3 rounded-xl border p-4"
    >
      <div className="flex flex-col gap-1">
        <p className="flex items-center gap-2 text-sm font-semibold tracking-wide uppercase">
          <Sparkles className="size-4 shrink-0" aria-hidden="true" />
          <span className="line-clamp-2">
            {t("creator_home.dormancy_nudge.headline", { brand: brand_name })}
          </span>
        </p>
        <p className="text-muted-foreground text-sm">
          {t("creator_home.dormancy_nudge.welcome_back", { name: first_name })}
        </p>
      </div>

      {/* One-line mission preview — Due segment omitted when no deadline. */}
      <p
        className="text-sm font-medium"
        data-testid="dormancy-nudge-mission-preview"
      >
        <span>
          {formatLabel} · {mission.title}
        </span>
        {dueLabel && (
          <span data-testid="dormancy-nudge-due">
            {" · "}
            {t("creator_home.dormancy_nudge.due", { date: dueLabel })}
          </span>
        )}
        <span>
          {" · "}
          {t("creator_home.dormancy_nudge.xp", { xp: mission.xp_reward })}
        </span>
      </p>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          asChild
          className="sm:flex-none"
          data-testid="dormancy-nudge-cta"
        >
          <Link href={creatorMissionDetailPath(mission.collab_id)}>
            {t("creator_home.dormancy_nudge.see_mission")}
          </Link>
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={handleDismiss}
          data-testid="dormancy-nudge-dismiss"
          className="sm:flex-none"
        >
          {t("creator_home.dormancy_nudge.not_now")}
        </Button>
      </div>
    </section>
  )
}
